import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { mailLink } from '@shared/support/supportConfig';

/**
 * Route-level feature gate (Access Control Phase 1). Renders children only if the
 * user has the required feature (admins bypass via hasFeature). Pass an array to
 * require ANY of the listed features (e.g. Browse needs any an_* analytics tab).
 *
 * Blocked state is always neutral. Paid plans were removed on 2026-08-02 (core features
 * are granted permanently at activation), so a missing feature means "not switched on for
 * this company" — never a price. The old UpgradePanel showed «Доступно по подписке … / мес»
 * to anyone who reached a gated URL directly.
 *
 * NOTE: this is UX enforcement only. The real backstop is has_feature() /
 * pgrst_analytics_guard on the RPCs — a technical user can still call the data
 * endpoints directly regardless of what the UI renders.
 */
export function FeatureGate({ feature, children }: { feature: string | string[]; children: ReactNode }) {
  const { hasFeature, loading } = useAuth();
  const { t } = useTranslation('common');
  if (loading) return null;
  const keys = Array.isArray(feature) ? feature : [feature];
  if (keys.some(hasFeature)) return <>{children}</>;

  return (
    <div style={{ padding: 40, textAlign: 'center', opacity: 0.8 }}>
      <h2 style={{ fontSize: 18, marginBottom: 8 }}>
        {t('access.noFeatureTitle', { defaultValue: 'Нет доступа к этому разделу' })}
      </h2>
      <p style={{ opacity: 0.7 }}>
        {t('access.noFeatureBody', {
          defaultValue: 'Эта функция не подключена для вашей компании. Обратитесь к администратору.',
        })}
      </p>
      <a href={mailLink('Подключить раздел')} style={{ color: 'var(--accent, #4a9eff)', textDecoration: 'underline', fontSize: 14 }}>
        {t('access.requestFeature', { defaultValue: 'Написать нам, чтобы подключить' })}
      </a>
    </div>
  );
}
